import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { HiOutlineArrowLeft, HiCheck, HiX, HiRefresh } from 'react-icons/hi';
import { MdEdit, MdDelete } from 'react-icons/md';
import moment from 'moment';

import { corporateService } from '../../services/corporation.service';
import GeneralModal from '../../helpers/GeneralModal';
import { createModal } from 'react-modal-promise';
import LogicContext from '../../Context/LogicContext';
import { GetRecoilApprovedList, SetRecoilApprovedList } from '../../store/customerApprovedAtom';
import CustomerApproveScroll from './customerApproveScroll';
import CustomerApproveToggle from './CustomerApproveToggle';
import CorpFileviewer from '../corporate_approval/CorpFileViewer';

export default function CustomerApproveDetails() {
  const navigate = useNavigate();
  const { customer_id } = useParams();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);

  const getRecoilData = GetRecoilApprovedList();
  const setRecoilData = SetRecoilApprovedList();

  const [customerData, setCustomerData] = useState({});
  const [selectedList, setSelectedList] = useState([]);

  const myPromiseModal = createModal(GeneralModal);

  const currModuleId = pathname.split('/')[1];
  const currModuleName = pathname.split('/')[2];
  const currUserMenu = pathname.split('/')[3];

  useEffect(() => {
    document.title = 'Customer Approval | Details';
    fetchCustomerDetails(customer_id);
  }, [customer_id]);

  const fetchCustomerDetails = async id => {
    try {
      const res = await corporateService.getCustomerApproveDetails(id);
      if (res.data?.[0]) {
        setCustomerData(res.data[0]);
      }
    } catch (err) {
      logicContext.showToast('Failed to load customer details', 'error');
    }
  };

  const goBack = () => navigate(-1);

  const updateStoreAndUI = updatedCustomer => {
    setCustomerData(updatedCustomer);

    const newList = getRecoilData.customer_info.map(record => (record?.Customer_Id === customer_id ? updatedCustomer : record));

    setRecoilData({ ...getRecoilData, customer_info: newList });
  };

  const confirmAndApprove = async () => {
    const result = await myPromiseModal({
      title: 'Approve Customer',
      body: 'Do you want to approve this customer request?',
      showInput: true,
      placeHolder: 'Approval remark (optional)',
    });

    if (result?.decision) {
      try {
        const res = await corporateService.approveOrCancelCustomerRequest(customer_id, 1, result.message || 'Approved');
        const data = logicContext.httpResponseManager(res.data[0]);
        if (data?.message_id === 1) {
          await fetchCustomerDetails(customer_id);
          logicContext.showToast(data.message_text, 'success');
        }
      } catch (err) {
        logicContext.showToast('Approval failed', 'error');
      }
    }
  };

  const confirmAndDecline = async () => {
    const result = await myPromiseModal({
      title: 'Decline Customer',
      body: 'Do you want to decline this customer request?',
      showInput: true,
      placeHolder: 'Decline reason (required)',
    });

    if (result?.decision) {
      if (!result.message?.trim()) {
        return logicContext.showToast('Remark is required for decline', 'warning');
      }

      try {
        const res = await corporateService.approveOrCancelCustomerRequest(customer_id, 2, result.message);
        const data = logicContext.httpResponseManager(res.data[0]);
        if (data?.message_id === 1) {
          await fetchCustomerDetails(customer_id);
          logicContext.showToast(data.message_text, 'success');
        }
      } catch (err) {
        logicContext.showToast('Decline failed', 'error');
      }
    }
  };

  const confirmAndResend = async () => {
    const result = await myPromiseModal({
      title: 'Resend Request',
      body: 'Do you want to resend the approval request?',
      showInput: true,
      placeHolder: 'Resend remark (optional)',
    });

    if (result?.decision) {
      try {
        const res = await corporateService.approveOrCancelCustomerRequest(customer_id, 3, result.message || 'Resent for approval');
        const data = logicContext.httpResponseManager(res.data[0]);
        if (data?.message_id === 1) {
          await fetchCustomerDetails(customer_id);
          logicContext.showToast(data.message_text, 'success');
        }
      } catch (err) {
        logicContext.showToast('Resend failed', 'error');
      }
    }
  };

  const handleAdminToggle = async shouldBeAdmin => {
    const targetStatus = shouldBeAdmin ? 1 : 2;
    try {
      const res = await corporateService.toggleAdminStatus(targetStatus, customer_id);
      const data = logicContext.httpResponseManager(res.data[0]);
      if (data?.message_id === 1) {
        await fetchCustomerDetails(customer_id);
        logicContext.showToast(data.message_text, 'success');
      }
    } catch (err) {
      logicContext.showToast('Failed to update admin status', 'error');
    }
  };

  const statusStyles = {
    1: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    2: 'bg-rose-100 text-rose-800 border-rose-200',
    3: 'bg-amber-100 text-amber-800 border-amber-200',
    4: 'bg-blue-100 text-blue-800 border-blue-200',
    5: 'bg-purple-100 text-purple-800 border-purple-200',
    6: 'bg-gray-100 text-gray-700 border-gray-200',
  };

  const getStatusBadge = () => {
    const id = customerData?.Customer_Approve_Status_Id;
    const name = customerData?.Customer_Approve_Status_Name || 'Unknown';

    return (
      <span
        className={`inline-flex rounded-full border px-4 py-1.5 text-sm font-medium ${
          statusStyles[id] || 'border-gray-200 bg-gray-100 text-gray-700'
        }`}
      >
        {name}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 pb-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <button onClick={goBack} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900">
                <HiOutlineArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Customer Approval</h1>
              {customerData?.Customer_Id && <span className="font-mono text-sm text-gray-500">#{customerData.Customer_Id}</span>}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={confirmAndApprove}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
              >
                <HiCheck size={18} />
                Approve
              </button>

              <button
                onClick={confirmAndDecline}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rose-700"
              >
                <HiX size={18} />
                Decline
              </button>

              <button
                onClick={confirmAndResend}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                <HiRefresh size={18} />
                Resend
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl pt-6">
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Sidebar - List */}
          <div className="flex-shrink-0 lg:w-80 xl:w-96">
            <div className="sticky top-20 overflow-hidden rounded-xl bg-white">
              <CustomerApproveScroll
                onSelection={list => setSelectedList(list.filter(i => i.checked))}
                viewItemDetails={id => {
                  navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${id}`);
                  fetchCustomerDetails(id);
                }}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="rounded-xl border border-gray-200 bg-white p-6 md:p-8">
              {/* Header */}
              <div className="flex flex-col gap-6 border-b pb-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{customerData?.Customer_Full_Name || 'Customer Approval'}</h2>
                  <div className="mt-2 flex items-center gap-4">
                    <span className="font-mono text-sm text-gray-500">ID: {customerData?.Customer_Id || '—'}</span>
                    <span className="text-sm text-gray-600">{customerData?.Customer_Type_Name || '—'}</span>
                  </div>
                </div>

                <div>{getStatusBadge()}</div>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-8 text-sm md:grid-cols-2 lg:grid-cols-3">
                {/* Corporate Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Corporate Information</h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-gray-600">Company Name</dt>
                        <dd className="mt-1 font-medium">
                          <Link
                            to={`/${currModuleId}/${currModuleName}/corporate/details/${customerData?.Corporate_Id}`}
                            className="text-blue-600 hover:underline"
                          >
                            {customerData?.Corporate_Name || '—'}
                          </Link>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">No. of Officers</dt>
                        <dd className="font-medium">{customerData?.Number_Of_Officer_Name || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Address</dt>
                        <dd className="text-gray-800">{customerData?.Corporate_Adddress || '—'}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Customer Information</h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-gray-600">Full Name</dt>
                        <dd className="font-medium">
                          <Link
                            to={`/${currModuleId}/${currModuleName}/customers/customer-details/${customer_id}`}
                            className="text-blue-600 hover:underline"
                          >
                            {customerData?.Customer_Full_Name || '—'}
                          </Link>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Email</dt>
                        <dd className="break-words font-medium">{customerData?.Customer_Email_Address || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Phone</dt>
                        <dd className="font-medium">{customerData?.Customer_Mobile_No || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Registration</dt>
                        <dd className="font-medium">{moment(customerData?.Encoded_Date).format('MMM D, YYYY • h:mm A') || '—'}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Approval & Admin */}
                <div className="space-y-6">
                  <div>
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Approval & Admin</h3>
                    <dl className="space-y-4">
                      <div>
                        <dt className="text-gray-600">Reviewed By</dt>
                        <dd className="font-medium">{customerData?.Encoded_By_Name || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Review Date</dt>
                        <dd className="font-medium">{moment(customerData?.Encoded_Date).format('MMM D, YYYY • h:mm A') || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Corporate Type</dt>
                        <dd className="font-medium">{customerData?.Corporate_Type_Name || '—'}</dd>
                      </div>
                      <div className="pt-2">
                        <dt className="mb-2 text-gray-600">Corporate Admin</dt>
                        <dd>
                          <CustomerApproveToggle
                            toggleItemAdminRole={handleAdminToggle}
                            val={customerData?.Customer_Corporate_Admin_Status_Id}
                          />
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="mt-10 border-t pt-8">
                <h3 className="mb-4 text-lg font-semibold text-gray-800">Documents</h3>
                <div className="rounded-lg border bg-gray-50 p-5">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-700">Corporate ID Upload</div>
                    <CorpFileviewer fileSource={customerData?.Customer_Corporate_Id_Upload_URL} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
